import React, { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { PageProps, Survey, SurveySet, Question } from '@/types'
import WelcomeScreen from '@/components/survey/WelcomeScreen'
import QuestionsScreen from '@/components/survey/QuestionsScreen'

interface SurveyShowProps extends PageProps {
  survey_set: SurveySet
  surveys: Survey[]
}

const Show: React.FC<SurveyShowProps> = ({ survey_set, surveys }) => {
  const [step, setStep] = useState('welcome')
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null)
  const [isFormValid, setIsFormValid] = useState(false)

  const { data, setData } = useForm({
    answer: {
      has_disability: null as boolean | null,
      disability_type: '',
      answer_questions_attributes: [] as Array<{ question_id: number; content: string }>
    }
  })

  const handleStart = (disabilityInfo: { hasDisability: boolean; disabilityType: string }) => {
    // Seleccionar la encuesta basándose en si tiene discapacidad
    const targetSurvey = disabilityInfo.hasDisability
      ? surveys.find(s => s.master_survey.name === 'accessibility')
      : surveys.find(s => s.master_survey.name === 'satisfaction')

    if (!targetSurvey) {
      console.error('No se encontró la encuesta correspondiente')
      return
    }

    setSelectedSurvey(targetSurvey)
    setData('answer', {
      has_disability: disabilityInfo.hasDisability,
      disability_type: disabilityInfo.disabilityType,
      answer_questions_attributes: targetSurvey.questions.map((q: Question) => ({
        question_id: q.id,
        content: ''
      }))
    })
    setStep('questions')
  }

  const validateForm = (formData: any) => {
    if (!selectedSurvey) return false

    // Obtener solo las preguntas requeridas
    const requiredQuestions = selectedSurvey.questions.filter(q => q.required)

    // Verificar que todas las preguntas requeridas tengan respuesta
    const allRequiredAnswered = requiredQuestions.every(question => {
      const answer = formData.answer.answer_questions_attributes.find((attr: any) => attr.question_id === question.id)
      return answer && answer.content && answer.content.trim() !== ''
    })

    return allRequiredAnswered
  }

  const handleDataChange = (newData: any) => {
    setData(newData)
    setIsFormValid(validateForm(newData))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSurvey) {
      console.error('No hay encuesta seleccionada')
      return
    }
    if (!isFormValid) {
      console.error('Formulario no válido: faltan respuestas requeridas')
      return
    }
    router.post(`/encuestas/${survey_set.hashid}/submit/${selectedSurvey.id}`, data)
  }

  return (
    <div className="bg-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {step === 'welcome' ? (
          <WelcomeScreen surveyName={survey_set.service_name} onStart={handleStart} />
        ) : selectedSurvey ? (
          <form onSubmit={handleSubmit}>
            <QuestionsScreen
              survey={selectedSurvey}
              surveySet={survey_set}
              initialData={data}
              onDataChange={handleDataChange}
            />
            <div className="flex justify-between items-center my-8">
              <button
                type="button"
                onClick={() => setStep('welcome')}
                className="px-6 py-3 cursor-pointer rounded-lg font-medium text-blue-600 border border-blue-600 hover:bg-blue-50 transition-colors"
              >
                Regresar
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  isFormValid
                    ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                }`}
              >
                Enviar encuesta
              </button>
            </div>
          </form>
        ) : (
          <div>Error: No se pudo cargar la encuesta</div>
        )}
      </div>
    </div>
  )
}

export default Show
